import { useEffect, useMemo, useState } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppContext } from '../../contexts/AppContext';
import { formatBRL } from '../agenda/cliente/List/helpers';
import { ServicosService } from './Service';
import { GripHorizontal } from 'lucide-react';

const service = new ServicosService();

const STATUS_ACTIVE = 1;

function SortableItem({ service }: { service: any }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: service._id });

    return (
        <li
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            className={isDragging ? 'relative z-10 bg-base-100 shadow-lg' : ''}
        >
            <div className="sf-row">
                {/* So a alca arrasta: o resto da linha continua rolando a lista no toque. */}
                <button
                    type="button"
                    className="-ml-2 flex h-10 w-8 shrink-0 cursor-grab touch-none items-center justify-center text-base-content/35"
                    aria-label={`Mover ${service.name}`}
                    {...attributes}
                    {...listeners}
                >
                    <GripHorizontal aria-hidden />
                </button>
                <span className="sf-row-body">
                    <span className="sf-row-title">{service.name}</span>
                </span>
                <span className="sf-row-end">{formatBRL(Number(service.price) || 0)}</span>
            </div>
        </li>
    );
}

export default function ServicosOrderConfig({
    data,
    callback,
    onCancel,
}: {
    data: any[];
    callback?: () => void | Promise<void>;
    onCancel?: () => void;
}) {
    const { successSnackbar, errorSnackbar } = useAppContext();

    const activeServices = useMemo(() => {
        return [...data]
            .filter((s) => s.status === STATUS_ACTIVE)
            .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));
    }, [data]);

    const [items, setItems] = useState<any[]>([]);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setItems(activeServices);
        setDirty(false);
    }, [activeServices]);

    const handleDragEnd = (event: any) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = items.findIndex((i) => i._id === active.id);
        const newIndex = items.findIndex((i) => i._id === over.id);

        const newItems = [...items];
        const [moved] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, moved);

        setItems(newItems);
        setDirty(true);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await service.updateOrder(
                items.map((s, index) => ({
                    id: s._id,
                    order: index + 1,
                }))
            );

            await callback?.();
            successSnackbar('Ordenação alterada com sucesso!');
            onCancel?.();
        } catch (error) {
            console.log(error);
            errorSnackbar('Ocorreu um erro ao editar a ordenação');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items.map((i) => i._id)} strategy={verticalListSortingStrategy}>
                    <ul className="sf-group">
                        {items.map((item) => (
                            <SortableItem key={item._id} service={item} />
                        ))}
                    </ul>
                </SortableContext>
            </DndContext>

            <div className="flex justify-end gap-2">
                <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
                    Cancelar
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
                    {saving && <span className="loading loading-spinner loading-sm"></span>}
                    Salvar ordem
                </button>
            </div>
        </div>
    );
}
